import { Col } from "antd";
import { Empty, Space, Tooltip, Row } from "antd";
import { UserAddOutlined } from "@ant-design/icons";
import AdminHOC from "../../../components/shared/HOC/AdminHOC";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import ConfirmDeletePopup from "../../../components/shared/confirmDeletePopup/ConfirmDeletePopup";
import { deleteSingleUser } from "../../../service/GroupService";
import Toast from "../../../components/shared/toast/Toast";
import AllocateCourseModal from "./AllocateCourse";
import { getEnrollmentSummary } from "../../../service/BundleService";
import CommonTable from "../../../components/shared/table/Table";
import CustomTab from "../../../components/shared/tab/TabComponent";
import { TabListGroup } from "../../../components/shared/config/TabListConstant";
import CustomButton from "../../../components/global/Button/CustomButton";
import CustomLabelDropdown from "../../../components/global/CustomLabelDropdown/CustomLabelDropdown";
import { useGroupUsers } from "../../../hooks/GroupHooks/useGetGroupUsers";
import usePagination from "../../../hooks/usePagination";
import searchIconsvg from "../../../assets/icons/Search-Icon.svg";
import {
  getGroupCourseColumns,
  getGroupUserColumns,
} from "../../../utility/coloumnConstants";
import backButton from "../../../assets/back-btn.svg";
import AddUserModal from "./AddUserModal";
import { COURSE_FILTER_OPTIONS } from "../../../assets/constants/constants";
import LottieIcon from "../../../components/shared/lottieIcon/LottieIcon";
import CustomInput from "../../../components/global/CustomInput/CustomInput.jsx";
import { debounceFunction } from "../../../assets/commonFunctions/CommonFunction.jsx";

const GroupHistory = ({ setLoading, setHeader }) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [groupName, setGroupName] = useState(location.state?.name || "N/A");

  const [activeTab, setActiveTab] = useState("1");
  const [showCourse, setShowCourse] = useState(false);
  const [courseFilter, setCourseFilter] = useState("all");
  const [dataLoaded, setDataLoaded] = useState(false);

  const [visiblePopover, setVisiblePopover] = useState(null);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
  const [allocatecourseModalOpen, setAllocateCourseModalOpen] = useState(false);

  const [deleteUser, setDeleteUser] = useState(null);
  const [userId, setUserId] = useState(null);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState(null);
  const [isInputHovered, setIsInputHovered] = useState(false);
  const [enrollmentList, setEnrollmentList] = useState([]);

  const { userList, fetchUsers } = useGroupUsers(id);
  const {
    currentPage,
    pageSize,
    total,
    setTotal,
    paginationConfig,
    setCurrentPage,
    handlePageChange,
  } = usePagination();
  const {
    currentPage: enrollPage,
    pageSize: enrollSize,
    total: enrollTotal,
    setTotal: setEnrollTotal,
    paginationConfig: enrollPaginationConfig,
    handlePageChange: handleEnrollPageChange,
  } = usePagination();

  const getUsers = async (page = currentPage, size = pageSize) => {
    setLoading(true);
    const data = await fetchUsers(page, size, searchText);
    if (data) setTotal(data.totalElements ?? data.totalPages * size);
    setLoading(false);
  };

  const getEnrollments = async (page = enrollPage, size = enrollSize) => {
    setLoading(true);

    const typeParam =
      courseFilter === "all" ? null : courseFilter.toUpperCase();

    const result = await getEnrollmentSummary(id, page, size, "asc", typeParam);

    if (result?.success) {
      const items = result.data.content.map((item, index) => ({
        id: item.id,
        srno: index + 1 + page * size,
        name: item.name,
        type: item.type,
        enrols: item.enrollmentCount,
        progress: item.averageProgress,
        isActive: item.isActive,
      }));
      setEnrollmentList(items);
      setEnrollTotal(result.data.totalElements);
    } else {
      console.error("Error fetching enrollment summary:", result?.data);
      setEnrollmentList([]);
    }

    setLoading(false);
  };

  const debouncedGetUsers = debounceFunction(() => {
    getUsers(0, pageSize);
  }, 500);

  useEffect(() => {
    debouncedGetUsers();
  }, [searchText]);

  useEffect(() => {
    getUsers(currentPage, pageSize);
  }, [currentPage, pageSize]);

  const handleViewUserClick = (record) => {
    if (!showCourse) {
      navigate(
        `/users/user-history/${record.id}?name=${encodeURIComponent(record.name)}`,
      );
    } else {
      if (record.type === "COURSE") {
        navigate(
          `/books/course-content/${record.id}?name=${encodeURIComponent(record.name)}`,
        );
      }
      if (record.type === "BUNDLE") {
        navigate(`/bundles-history/${record.id}`, {
          state: { name: record.name },
        });
      }
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(0);
  };

  const handleCloseModal = () => setIsModalOpen(false);
  const handleCloseAllocationModal = () => setAllocateCourseModalOpen(false);

  const handleAddNew = async () => {
    await getEnrollments();
    setIsModalOpen(true);
  };

  useEffect(() => {
    async function fetchAllData() {
      await getUsers();
      setDataLoaded(true);
    }
    fetchAllData();
  }, [id]);

  const handleAllocateCourse = (record) => {
    setUserId(record.id);
    setAllocateCourseModalOpen(true);
  };

  const handleOpenConfirmDeletePopup = (user) => {
    setIsConfirmPopupOpen(true);
    setDeleteUser(user);
  };
  const handleDeleteUser = async () => {
    setLoading(true);
    const groupdetails = { groupId: id, userId: deleteUser.id };
    const result = await deleteSingleUser(groupdetails);

    if (result?.success) {
      setToastMessage("User removed successfully!");
      setToastType("success");
      setShowToast(true);
      await getUsers();
    } else {
      setToastMessage(
        result?.data || "Error occurred while deleting the User.",
      );
      setToastType("error");
      setShowToast(true);
    }

    setIsConfirmPopupOpen(false);
    setDeleteUser(null);
    setLoading(false);
  };

  useEffect(() => {
    if (showCourse) {
      getEnrollments(0, enrollSize);
    }
  }, [courseFilter, showCourse]);

  const userColumns = getGroupUserColumns({
    handleViewUserClick,
    handleAllocateCourse,
    handleOpenConfirmDeletePopup,
    visiblePopover,
    setVisiblePopover,
    hoveredButton,
    setHoveredButton,
  });

  const courseColumns = getGroupCourseColumns({
    handleViewUserClick,
    courseFilter,
  });
  const handleTabChange = (key) => {
    setActiveTab(key);
    setShowCourse(key === "2");
    setCurrentPage(0);
   setSearchText("");
  };
  return (
    <div className="p-6 flex flex-col border border-[#E8E8E8] rounded-[10px] shadow-[0px_0px_6px_0px_rgba(0,0,0,0.05)] gap-6">
      <div className="flex flex-col gap-6">
        <div className="flex justify-between">
          <h1 className="text-[#e74860] text-[1.5rem] font-normal">
            {groupName}-Group History
          </h1>
          <div
            className="flex gap-2 justify-center cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <img alt="back-btn" src={backButton} />
            <span>Back</span>
          </div>
        </div>

        <div>
          <Row>
            <Col span={8}>
              <CustomTab entries={TabListGroup} onChange={handleTabChange} />
            </Col>

            <Col span={16}>
              <div className="flex gap-[10px] justify-end">
                {activeTab === "1" && (
                <div
                  onMouseEnter={() => setIsInputHovered(true)}
                  onMouseLeave={() => setIsInputHovered(false)}
                >
                  <CustomInput
                    key={activeTab}
                    className="w-[14vw]"
                    placeholder="Search"
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    icon={
                      isInputHovered
                        ? <LottieIcon name="searchIcon" size={24} shouldPlay={true} />
                        : <img src={searchIconsvg} alt="search" className="w-6 h-6" />
                    }
                    allowClear
                  />
                </div>
                )}
                <Space>
                  {!showCourse && (
                    <Tooltip title="Add user to the group">
                      <CustomButton
                        icon={<UserAddOutlined />}
                        onClick={handleAddNew}
                        variant="primary"
                      >
                        Add User
                      </CustomButton>
                    </Tooltip>
                  )}
                </Space>
                 <CustomButton
                  onClick={() => navigate(`/group-users/${id}`)}
                  variant="primary"
                >
                  User Report
                </CustomButton>
                {showCourse && (
                  <CustomLabelDropdown
                    allowClear={false}
                    wrapperClass="w-[200px]"
                    value={courseFilter}
                    onChange={(value) => setCourseFilter(value)}
                    placeholder="Select Filter"
                    options={COURSE_FILTER_OPTIONS}
                  />
                )}
              </div>
            </Col>

            <Row className="w-full">
              <div className="w-full">
                {activeTab === "1" && (
                  <div>
                    <CommonTable
                      data={userList}
                      columns={userColumns}
                      scroll={{ y: "48vh"}}
                      className="hide-scrollbar top-table-radius"
                      pagination={{
                        ...paginationConfig,
                        showSizeChanger: true,
                      }}
                      onChange={(pagination) => {
                        handlePageChange(pagination);
                        getUsers(pagination.current - 1, pagination.pageSize);
                      }}
                      serverSide={true}
                      showLoader={!dataLoaded}
                    />
                  </div>
                )}

                {activeTab === "2" && (
                  <div>
                    <CommonTable
                      data={enrollmentList}
                      columns={courseColumns}
                      scroll={{ y: "48vh"}}
                      className="hide-scrollbar top-table-radius"
                      pagination={{
                        ...enrollPaginationConfig,
                        showSizeChanger: true,
                      }}
                      onChange={(pagination) => {
                        handleEnrollPageChange(pagination);
                        getEnrollments(
                          pagination.current - 1,
                          pagination.pageSize,
                        );
                      }}
                      serverSide={true}
                      showLoader={!dataLoaded}
                    />
                  </div>
                )}
              </div>
            </Row>
          </Row>
        </div>
      </div>

      <AddUserModal
        isModalOpen={isModalOpen}
        getUsers={getUsers}
        handleCloseModal={handleCloseModal}
        setShowToast={setShowToast}
        setToastMessage={setToastMessage}
        setToastType={setToastType}
        setLoading={setLoading}
        groupId={id}
        courses={enrollmentList.filter((e) => e.type === "COURSE")}
        bundles={enrollmentList.filter((e) => e.type === "BUNDLE")}
        getBundles={getEnrollments}
        getCourses={getEnrollments}
      />

      <Toast
        message={toastMessage}
        type={toastType}
        show={showToast}
        onClose={() => setShowToast(false)}
      />

      <ConfirmDeletePopup
        isOpen={isConfirmPopupOpen}
        onClose={() => setIsConfirmPopupOpen(false)}
        onConfirm={handleDeleteUser}
      />

      <AllocateCourseModal
        isModalOpen={allocatecourseModalOpen}
        groupId={id}
        userId={userId}
        getUsers={getUsers}
        getCourses={getEnrollments}
        getBundles={getEnrollments}
        handleCloseModal={handleCloseAllocationModal}
        setToastMessage={setToastMessage}
        setToastType={setToastType}
        setShowToast={setShowToast}
        setLoading={setLoading}
      />
    </div>
  );
};

export default AdminHOC(GroupHistory);
